import { React, useState, useEffect } from 'react';
import axios from 'axios';
import '../../App.css'
import { Grid, TextField, Box, Button, Paper, Stack, Typography, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const initialRowValues = {
    id: 0,
    startRange: '',
    endRange: '',
    taxRate: '',
}

const RequiredLabel = ({ label }) => (
    <span>
        {label}
        <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
    </span>
);

// Generate financial year options from 2020-2021 to 2029-2030
const generateFinancialYears = () => {
    const years = [];
    for (let startYear = 2020; startYear <= 2029; startYear++) {
        years.push(`${startYear}-${startYear + 1}`);
    }
    return years;
};

const financialYears = generateFinancialYears();

// Get current financial year
const getCurrentFinancialYear = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11, where 0 is January
    
    // Financial year starts from April (month 3)
    if (currentMonth >= 3) {
        // April to December - current year to next year
        return `${currentYear}-${currentYear + 1}`;
    } else {
        // January to March - previous year to current year
        return `${currentYear - 1}-${currentYear}`;
    }
};

function AddTDSConfigurationForm(props) {
    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
    const navigate = useNavigate();

    const [rows, setRows] = useState([{ ...initialRowValues }]);
    const [errors, setErrors] = useState({});
    const [financialYear, setFinancialYear] = useState("2025-2026"); // Hard coded financial year

    useEffect(() => {
        if (notify.isOpen && notify.type === 'success') {
          // Close the modal after a short delay to show the notification
          const timer = setTimeout(() => {
            // Call the parent's function to close the modal
            if (props.onCloseModal) {
              props.onCloseModal();
            }
          }, 100);
          
          return () => clearTimeout(timer);
        }
      }, [notify, props.onCloseModal]);

    useEffect(() => {
        async function getTDSConfig() {
            if (props.fetch_id) {
                try {
                    // Fetch all tax ranges for the financial year and find the matching one
                    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/configure/get-all-tax-ranges/2025-2026`);
                    if (response.status === 200 && response.data && response.data.data) {
                        // Find the tax range with matching tax_details_id (handle both string and number comparison)
                        const fetchIdNum = Number(props.fetch_id);
                        const tdsData = response.data.data.find(item => {
                            const itemId = Number(item.tax_details_id);
                            return itemId === fetchIdNum || item.tax_details_id === props.fetch_id;
                        });
                        if (tdsData) {
                            // Handle 0 values properly - 0 is a valid value, so check for null/undefined
                            const minAmount = tdsData.min_amount !== null && tdsData.min_amount !== undefined 
                                ? String(tdsData.min_amount) 
                                : (tdsData.startRange || tdsData.start_range || tdsData.StartRange || '');
                            const maxAmount = tdsData.max_amount !== null && tdsData.max_amount !== undefined 
                                ? String(tdsData.max_amount) 
                                : (tdsData.endRange || tdsData.end_range || tdsData.EndRange || '');
                            const taxRate = tdsData.tax_rate !== null && tdsData.tax_rate !== undefined 
                                ? String(tdsData.tax_rate) 
                                : (tdsData.taxRate || tdsData.TaxRate || '');
                            
                            setRows([{
                                id: props.fetch_id,
                                startRange: minAmount,
                                endRange: maxAmount,
                                taxRate: taxRate,
                            }]);
                            // Handle financial year
                            const effectiveDateValue = tdsData.financial_year || tdsData.effectiveDate || tdsData.effective_date || tdsData.EffectiveDate || '';
                            if (effectiveDateValue) {
                                // If it's a date string, convert to financial year format
                                if (effectiveDateValue.includes('-') && effectiveDateValue.length === 10) {
                                    const date = new Date(effectiveDateValue);
                                    const year = date.getFullYear();
                                    const month = date.getMonth();
                                    if (month >= 3) {
                                        setFinancialYear(`${year}-${year + 1}`);
                                    } else {
                                        setFinancialYear(`${year - 1}-${year}`);
                                    }
                                } else {
                                    // Assume it's already in financial year format
                                    setFinancialYear(effectiveDateValue);
                                }
                            } else {
                                setFinancialYear("2025-2026");
                            }
                        } else {
                            // If not found, show error
                            setNotify({
                                isOpen: true,
                                message: 'Tax range not found',
                                type: 'error'
                            });
                        }
                    }
                } catch (e) {
                    if (e.response && e.response.status === 401) {
                        setNotify({
                            isOpen: true,
                            message: 'Session Expired!',
                            type: 'error'
                        });
                        setTimeout(() => {
                            navigate('/');
                        }, 1000);
                    } else {
                        setNotify({
                            isOpen: true,
                            message: 'Failed to load tax range data',
                            type: 'error'
                        });
                    }
                }
            } else {
                // Reset to single row when adding new
                setRows([{ ...initialRowValues }]);
                setFinancialYear("2025-2026");
            }
        }
        getTDSConfig();
    }, [props.fetch_id, navigate]);

    const validate = () => {
        let temp = {}
        let isValid = true;

        rows.forEach((row, index) => {
            const rowPrefix = `row_${index}_`;
            
            // Validate start range
            if (row.startRange === '' || row.startRange === null || row.startRange === undefined) {
                temp[`${rowPrefix}startRange`] = "This field is required.";
                isValid = false;
            } else {
                const startRangeNum = parseFloat(row.startRange);
                // Start range can be zero, but cannot be negative
                if (isNaN(startRangeNum) || startRangeNum < 0) {
                    temp[`${rowPrefix}startRange`] = "Start range cannot be negative.";
                    isValid = false;
                }
            }
            
            // Validate end range
            if (row.endRange === '' || row.endRange === null || row.endRange === undefined) {
                temp[`${rowPrefix}endRange`] = "This field is required.";
                isValid = false;
            } else {
                const endRangeNum = parseFloat(row.endRange);
                // End range cannot be negative
                if (isNaN(endRangeNum) || endRangeNum < 0) {
                    temp[`${rowPrefix}endRange`] = "End range cannot be negative.";
                    isValid = false;
                }
            }
            
            // Validate tax rate
            if (!row.taxRate) {
                temp[`${rowPrefix}taxRate`] = "This field is required.";
                isValid = false;
            } else {
                const taxRateNum = parseFloat(row.taxRate);
                if (isNaN(taxRateNum) || taxRateNum < 0 || taxRateNum > 100) {
                    temp[`${rowPrefix}taxRate`] = "Tax rate must be a number between 0 and 100.";
                    isValid = false;
                }
            }

            // Validate that start range cannot be more than end range
            if (row.startRange !== '' && row.endRange !== '') {
                const startRangeNum = parseFloat(row.startRange);
                const endRangeNum = parseFloat(row.endRange);
                if (!isNaN(startRangeNum) && !isNaN(endRangeNum)) {
                    if (startRangeNum > endRangeNum) {
                        temp[`${rowPrefix}endRange`] = "Start range cannot be more than end range.";
                        isValid = false;
                    } else if (startRangeNum === endRangeNum) {
                        temp[`${rowPrefix}endRange`] = "End range must be greater than start range.";
                        isValid = false;
                    }
                }
            }
        });

        // Validate financial year
        if (!financialYear) {
            temp.financialYear = "This field is required.";
            isValid = false;
        }

        setErrors(temp);
        return isValid;
    }

    const resetForm = () => {
        setRows([{ ...initialRowValues }]);
        setFinancialYear("2025-2026");
        setErrors({});
    }

    const addRow = () => {
        setRows([...rows, { ...initialRowValues }]);
    }

    const removeRow = (index) => {
        if (rows.length > 1) {
            const newRows = rows.filter((_, i) => i !== index);
            setRows(newRows);
            // Clear errors for removed row
            const newErrors = { ...errors };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`row_${index}_`)) {
                    delete newErrors[key];
                }
            });
            setErrors(newErrors);
        }
    }

    function handleInputChange(e, index) {
        const { name, value } = e.target;
        const newRows = [...rows];
        newRows[index] = {
            ...newRows[index],
            [name]: value
        };
        setRows(newRows);
        
        // Clear error for this field
        const errorKey = `row_${index}_${name}`;
        const newErrors = { ...errors };
        if (newErrors[errorKey]) {
            delete newErrors[errorKey];
        }
        
        // If startRange or endRange changes, also clear the comparison error on endRange
        if (name === 'startRange' || name === 'endRange') {
            const endRangeErrorKey = `row_${index}_endRange`;
            if (newErrors[endRangeErrorKey] && 
                (newErrors[endRangeErrorKey].includes('cannot be more than') || 
                 newErrors[endRangeErrorKey].includes('must be greater than'))) {
                delete newErrors[endRangeErrorKey];
            }
        }
        
        setErrors(newErrors);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            if (props.fetch_id) {
                // Edit existing TDS configuration (single row)
                const row = rows[0];
                const payload = {
                    min_amount: parseFloat(row.startRange),
                    max_amount: parseFloat(row.endRange),
                    tax_rate: parseFloat(row.taxRate),
                    financial_year: financialYear,
                    tax_details_id: props.fetch_id,
                };

                axios.post(`${process.env.REACT_APP_API_BASE_URL}/configure/update-tax-range`, payload)
                    .then(function (response) {
                        if (response.status === 200) {
                            // Trigger refresh callback immediately
                            if (props.onRefresh) {
                                props.onRefresh();
                            }
                            // Show success notification
                            setNotify({
                                isOpen: true,
                                message: response.data.message || 'TDS Configuration updated successfully',
                                type: 'success'
                            });
                            // Close modal immediately
                            if (props.setOpenPopup) {
                                props.setOpenPopup(false);
                            }
                            if (props.onCloseModal) {
                                props.onCloseModal();
                            }
                        }
                    })
                    .catch(function (e) {
                        handleApiError(e);
                    });
            } else {
                // Add new TDS configurations (can be multiple)
                const promises = rows.map(row => {
                    const payload = {
                        min_amount: parseFloat(row.startRange),
                        max_amount: parseFloat(row.endRange),
                        tax_rate: parseFloat(row.taxRate),
                        financial_year: financialYear,
                    };
                    return axios.post(`${process.env.REACT_APP_API_BASE_URL}/configure/add-tax-range`, payload);
                });

                Promise.all(promises)
                    .then(function (responses) {
                        const allSuccess = responses.every(response => response.status === 201);
                        if (allSuccess) {
                            // Trigger refresh callback immediately to call GET ALL API
                            if (props.onRefresh) {
                                props.onRefresh();
                            }
                            // Reset form
                            resetForm();
                            // Show success notification
                            setNotify({
                                isOpen: true,
                                message: rows.length > 1 
                                    ? `${rows.length} TDS Configurations added successfully`
                                    : 'TDS Configuration added successfully',
                                type: 'success'
                            });
                            // Close modal immediately
                            if (props.setOpenPopup) {
                                props.setOpenPopup(false);
                            }
                            if (props.onCloseModal) {
                                props.onCloseModal();
                            }
                        }
                    })
                    .catch(function (e) {
                        handleApiError(e);
                    });
            }
        }
    }

    const handleApiError = (e) => {
        if (e.response && e.response.status === 401) {
            setNotify({
                isOpen: true,
                message: 'Session Expired!',
                type: 'error'
            });
            setTimeout(() => {
                navigate('/');
            }, 1000);
        } else if (e.response && e.response.status === 422) {
            setNotify({
                isOpen: true,
                message: e.response.data.error_message?.sqlMessage || e.response.data.error_message || 'An error occurred',
                type: 'error'
            });
        } else {
            setNotify({
                isOpen: true,
                message: 'An error occurred. Please try again.',
                type: 'error'
            });
        }
    }

    return (
        <Box sx={{ display: "flex", justifyContent: "center", margin: "-10px", padding: "30px", fontWeight: "500px" }}>
            <Paper variant="outlined" elevation={5} sx={{ width: "700px", marginTop: "-20px" }}>
                <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
                    <Stack spacing={2} sx={{ margin: "30px"}}>
                        {rows.map((row, index) => (
                            <Box key={index} sx={{ position: 'relative' }}>
                                {/* First Row: Start Range, End Range, Tax Rate */}
                                <Grid container spacing={2} style={{ marginLeft: "25px", marginBottom: "15px" }} alignItems="flex-start">
                                    <Grid item xs={3.5}>
                                        <TextField
                                            variant="outlined"
                                            name="startRange"
                                            label={<RequiredLabel label="Start Range (&#8377;)" />}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            inputProps={{ min: "0", step: "0.01" }}
                                            value={row.startRange}
                                            onChange={(e) => handleInputChange(e, index)}
                                            {...(errors[`row_${index}_startRange`] && { 
                                                error: true, 
                                                helperText: errors[`row_${index}_startRange`] 
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={3.5}>
                                        <TextField
                                            variant="outlined"
                                            name="endRange"
                                            label={<RequiredLabel label="End Range (&#8377;)" />}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            inputProps={{ min: "0", step: "0.01" }}
                                            value={row.endRange}
                                            onChange={(e) => handleInputChange(e, index)}
                                            {...(errors[`row_${index}_endRange`] && { 
                                                error: true, 
                                                helperText: errors[`row_${index}_endRange`] 
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={3.5}>
                                        <TextField
                                            variant="outlined"
                                            name="taxRate"
                                            label={<RequiredLabel label="Tax Rate (%)" />}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            inputProps={{ step: "0.01", min: "0", max: "100" }}
                                            value={row.taxRate}
                                            onChange={(e) => handleInputChange(e, index)}
                                            {...(errors[`row_${index}_taxRate`] && { 
                                                error: true, 
                                                helperText: errors[`row_${index}_taxRate`] 
                                            })}
                                        />
                                    </Grid>
                                    {rows.length > 1 && !props.fetch_id && (
                                        <Grid item xs={1.5}>
                                            <IconButton
                                                color="error"
                                                onClick={() => removeRow(index)}
                                                sx={{ mt: 0.5 }}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    )}
                                </Grid>
                                {/* Second Row: Financial Year */}
                                <Grid container spacing={2} style={{ marginLeft: "25px" }} alignItems="flex-start">
                                    <Grid item xs={3.5}>
                                        {index === 0 && (
                                            <FormControl 
                                                variant="outlined" 
                                                size="small" 
                                                fullWidth
                                                error={!!errors.financialYear}
                                            >
                                                <InputLabel id="financial-year-label">
                                                    Financial Year <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
                                                </InputLabel>
                                                <Select
                                                    labelId="financial-year-label"
                                                    value={financialYear}
                                                    onChange={(e) => {
                                                        setFinancialYear(e.target.value);
                                                        if (errors.financialYear) {
                                                            const newErrors = { ...errors };
                                                            delete newErrors.financialYear;
                                                            setErrors(newErrors);
                                                        }
                                                    }}
                                                    label="Financial Year *"
                                                >
                                                    {financialYears.map((year) => (
                                                        <MenuItem key={year} value={year}>
                                                            {year}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.financialYear && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                        {errors.financialYear}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}

                        <Grid container className="space-around" sx={{ mt: 2 }} style={{ justifyContent: "center" , marginTop: "20px" }}>
                            <Grid item>
                                <Button variant="contained" color="error" onClick={resetForm}>Reset</Button>
                            </Grid>
                            <Grid item>
                                <Button style={{ marginLeft: "10px" }} variant="contained" type="submit" color="success">
                                    {props.fetch_id ? 'Update' : 'Submit'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Stack>
                </form>
            </Paper>
            <Notification
                notify={notify}
                setNotify={setNotify}
            />
        </Box>
    )
}

export default AddTDSConfigurationForm;

